import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AnalyticsSummary, ClickStatRow, PostAnalyticsRow, PostDetail } from '@zen-bell/shared';
import { adminFetch } from './lib/api';
import { LoginForm } from './components/LoginForm';
import { PostForm } from './components/PostForm';
import { StatsDashboard } from './components/StatsDashboard';
import { ContentAnalytics } from './components/ContentAnalytics';
import { RangePicker, lastDays, percent, query, type DateRange } from './components/RangePicker';

type Tab = 'dashboard' | 'blog';
type View = { name: 'list' } | { name: 'form'; post: PostDetail | null };

// Sortable analytics columns of the post list; 'date' keeps the API's newest-first order.
type SortKey = 'date' | 'views' | 'app_store' | 'google_play' | 'download' | 'conversion';
const METRIC_COLUMNS: { key: Exclude<SortKey, 'date'>; label: string }[] = [
  { key: 'views', label: 'Views' },
  { key: 'app_store', label: 'App Store' },
  { key: 'google_play', label: 'Google Play' },
  { key: 'download', label: 'Download' },
  { key: 'conversion', label: 'Conv.' },
];

const STATUS_LABEL: Record<PostDetail['status'], string> = {
  published: 'Published',
  draft: 'Draft',
  hidden: 'Hidden',
};

export function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>('dashboard');
  const [view, setView] = useState<View>({ name: 'list' });
  const [posts, setPosts] = useState<PostDetail[] | null>(null);
  const [stats, setStats] = useState<ClickStatRow[] | null>(null);
  const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [listRange, setListRange] = useState<DateRange>(() => lastDays(30));
  const [postStats, setPostStats] = useState<Map<string, PostAnalyticsRow>>(new Map());
  const [sort, setSort] = useState<SortKey>('date');

  const notify = useCallback((text: string, error = false) => {
    setToast({ text, error });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    const [postsRes, statsRes] = await Promise.all([
      adminFetch<{ posts: PostDetail[] }>('/blog'),
      adminFetch<{ data: ClickStatRow[] }>('/stats?days=400'),
    ]);
    setPosts(postsRes.posts);
    setStats(statsRes.data);
  }, []);

  // The session lives in an HttpOnly cookie that JavaScript cannot read, so the
  // only way to know whether we are signed in is to ask the API.
  useEffect(() => {
    adminFetch('/stats?days=1')
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed) void load().catch(() => notify('Failed to load data', true));
  }, [authed, load, notify]);

  // Per-post analytics for the Blog Manager columns. Reloaded with the list so
  // a new post gets its (empty) row.
  useEffect(() => {
    if (!authed || posts === null) return;
    adminFetch<AnalyticsSummary>(`/analytics/summary?${query(listRange)}`)
      .then((d) => setPostStats(new Map(d.posts.map((p) => [p.postId, p]))))
      .catch(() => setPostStats(new Map()));
  }, [authed, posts, listRange]);

  const sortedPosts = useMemo(() => {
    if (!posts || sort === 'date') return posts;
    const value = (p: PostDetail) => postStats.get(p.id)?.[sort] ?? 0;
    return [...posts].sort((a, b) => value(b) - value(a));
  }, [posts, postStats, sort]);

  if (authed === null) return <div className="loading">Loading…</div>;
  if (!authed) return <LoginForm onSuccess={() => setAuthed(true)} />;

  const remove = async (post: PostDetail) => {
    if (!confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminFetch(`/blog/${post.id}`, { method: 'DELETE' });
      notify('Post deleted');
      await load();
    } catch {
      notify('Delete failed', true);
    }
  };

  // Publish/hide straight from the list, as the original admin did.
  const toggleStatus = async (post: PostDetail) => {
    const status = post.status === 'hidden' ? 'published' : 'hidden';
    try {
      await adminFetch('/blog', {
        method: 'POST',
        body: JSON.stringify({ ...post, status }),
      });
      notify(status === 'published' ? 'Post published' : 'Post hidden');
      await load();
    } catch {
      notify('Update failed', true);
    }
  };

  const sendReport = async () => {
    setSendingReport(true);
    try {
      await adminFetch('/report/send', { method: 'POST' });
      notify('Report sent to MS Teams');
    } catch (err) {
      notify(`Report failed: ${(err as Error).message}`, true);
    } finally {
      setSendingReport(false);
    }
  };

  const signOut = async () => {
    await adminFetch('/auth/logout', { method: 'POST' }).catch(() => {});
    setAuthed(false);
  };

  return (
    <div id="dashboard-container">
      <nav className="nav-tabs">
        <button
          id="tab-dashboard"
          className={`nav-tab${tab === 'dashboard' ? ' active' : ''}`}
          onClick={() => setTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          id="tab-blog"
          className={`nav-tab${tab === 'blog' ? ' active' : ''}`}
          onClick={() => setTab('blog')}
        >
          Blog Manager
        </button>
        <button id="sign-out" className="nav-tab nav-tab-end" onClick={() => void signOut()}>
          Sign out
        </button>
      </nav>

      {tab === 'dashboard' ? (
        <div id="view-dashboard">
          <StatsDashboard
            rows={stats}
            onRefresh={() => void load().catch(() => notify('Failed to load data', true))}
            onSendReport={() => void sendReport()}
            sendingReport={sendingReport}
          />
          <ContentAnalytics />
        </div>
      ) : null}

      {tab === 'blog' ? (
        <div id="view-blog" className="blog-manager">
          {view.name === 'list' ? (
            <>
              <div className="header">
                <h2>Blog Manager</h2>
                <div>
                  <RangePicker value={listRange} onChange={setListRange} />
                  <button
                    id="btn-show-create-post"
                    className="btn-inline"
                    onClick={() => setView({ name: 'form', post: null })}
                  >
                    + Create Post
                  </button>
                </div>
              </div>
              <div id="post-list-view" className="panel">
                <table id="posts-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Status</th>
                      {METRIC_COLUMNS.map((c) => (
                        <th key={c.key} className="num">
                          <button
                            type="button"
                            className={`sort-btn${sort === c.key ? ' active' : ''}`}
                            title={`Sort by ${c.label}`}
                            onClick={() => setSort(sort === c.key ? 'date' : c.key)}
                          >
                            {c.label}{sort === c.key ? ' ↓' : ''}
                          </button>
                        </th>
                      ))}
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody id="posts-table-body">
                    {sortedPosts === null ? (
                      <tr><td colSpan={9} className="empty-state">Loading posts...</td></tr>
                    ) : sortedPosts.length === 0 ? (
                      <tr><td colSpan={9} className="empty-state">No posts found.</td></tr>
                    ) : (
                      sortedPosts.map((post) => (
                        <tr key={post.id}>
                          <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                          <td className="post-title">{post.title}</td>
                          <td>
                            <span className={`status status-${post.status}`}>{STATUS_LABEL[post.status]}</span>
                          </td>
                          {METRIC_COLUMNS.map((c) => {
                            const v = postStats.get(post.id)?.[c.key] ?? 0;
                            return (
                              <td key={c.key} className="num">
                                {c.key === 'conversion' ? percent(v) : v}
                              </td>
                            );
                          })}
                          <td className="actions-col">
                            <button
                              className="action-btn"
                              title="Edit"
                              aria-label={`Edit ${post.title}`}
                              onClick={() => setView({ name: 'form', post })}
                            >
                              <i className="fa fa-pencil" aria-hidden="true" />
                            </button>
                            <button
                              className="action-btn"
                              title={post.status === 'hidden' ? 'Unhide (Publish)' : 'Hide'}
                              aria-label={`${post.status === 'hidden' ? 'Publish' : 'Hide'} ${post.title}`}
                              onClick={() => void toggleStatus(post)}
                            >
                              <i className={`fa ${post.status === 'hidden' ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
                            </button>
                            <button
                              className="action-btn delete"
                              title="Delete"
                              aria-label={`Delete ${post.title}`}
                              onClick={() => void remove(post)}
                            >
                              <i className="fa fa-trash" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <PostForm
              post={view.post}
              onCancel={() => setView({ name: 'list' })}
              onSaved={async (status) => {
                setView({ name: 'list' });
                notify(status === 'draft' ? 'Draft saved successfully!' : 'Post published successfully!');
                await load();
              }}
            />
          )}
        </div>
      ) : null}

      <div id="toast" className={`toast${toast ? ' show' : ''}${toast?.error ? ' error' : ''}`}>
        {toast?.text}
      </div>
    </div>
  );
}
